import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollText, CheckCircle2, AlertTriangle, Clock, BookOpen, ExternalLink, Download, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FILE_TIMEOUT = 120000;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS = {
  Overdue: { cls: "bg-red-100 text-red-800", icon: AlertTriangle, label: "Overdue — please accept" },
  Pending: { cls: "bg-amber-100 text-amber-800", icon: Clock, label: "Please read & accept" },
  Accepted: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Accepted" },
  "Info only": { cls: "bg-slate-100 text-slate-600", icon: BookOpen, label: "For your information" },
};

function ReadDialog({ policy, statement, onClose }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!policy) return undefined;
    let href = null;
    let cancelled = false;
    setUrl(null); setLoadError(false); setAgreed(false);
    // Fetching the file is also what records "opened" on the server —
    // the Accept button is refused until this has happened.
    api.get(`/my/policies/${policy.id}/file`, { responseType: "blob", timeout: FILE_TIMEOUT })
      .then((res) => {
        if (cancelled) return;
        href = URL.createObjectURL(res.data);
        setUrl(href);
        qc.invalidateQueries({ queryKey: ["myPolicies"] });
      })
      .catch(() => !cancelled && setLoadError(true));
    return () => { cancelled = true; if (href) setTimeout(() => URL.revokeObjectURL(href), 1000); };
  }, [policy, qc]);

  const accept = useMutation({
    mutationFn: async () => (await api.post(`/my/policies/${policy.id}/acknowledge`, { version: policy.version, confirm: true })).data,
    onSuccess: () => {
      toast.success(`Thank you — you have accepted ${policy.title}`);
      qc.invalidateQueries({ queryKey: ["myPolicies"] });
      qc.invalidateQueries({ queryKey: ["myPolicyStatus"] });
      onClose();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (!policy) return null;
  const canPreview = ["pdf", "png", "jpg", "jpeg", "webp"].includes(policy.ext);
  const isImage = ["png", "jpg", "jpeg", "webp"].includes(policy.ext);
  const needsAccept = policy.status === "Pending" || policy.status === "Overdue";

  return (
    <Dialog open={!!policy} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl h-[94vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="pr-6">{policy.title} <span className="text-slate-400 font-normal text-base">v{policy.version}</span></DialogTitle>
          <DialogDescription>
            {policy.category}{policy.effective_date && ` · Effective ${fmtDate(policy.effective_date)}`}
            {policy.accepted_previous_version && policy.status !== "Accepted" && policy.change_note && (
              <span className="block mt-1 text-amber-700">Updated since you last accepted it: {policy.change_note}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden">
          {loadError ? (
            <div className="h-full grid place-items-center text-center p-6">
              <div><AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" /><p className="text-sm text-slate-600">Couldn't open the document. Please close this and try again.</p></div>
            </div>
          ) : !url ? (
            <div className="h-full grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : canPreview ? (
            isImage ? <div className="h-full overflow-auto"><img src={url} alt={policy.title} className="w-full" /></div>
              : <iframe title={policy.title} src={url} className="w-full h-full bg-white" />
          ) : (
            <div className="h-full grid place-items-center text-center p-6">
              <div>
                <BookOpen className="h-8 w-8 text-teal mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-3">This document ({policy.ext?.toUpperCase()}) can't be shown here. Download it and read it fully.</p>
                <a href={url} download={policy.file_name}><Button className="bg-teal hover:bg-teal/90 text-white"><Download className="h-4 w-4 mr-1" /> Download to read</Button></a>
              </div>
            </div>
          )}
        </div>
        {url && canPreview && (
          <p className="text-[11px] text-slate-400 -mt-1">Document not showing properly on your phone? <a href={url} target="_blank" rel="noreferrer" className="text-teal underline inline-flex items-center gap-0.5">Open it in a new tab <ExternalLink className="h-3 w-3" /></a> or <a href={url} download={policy.file_name} className="text-teal underline">download it</a>.</p>
        )}

        {needsAccept ? (
          <div className="rounded-md border border-teal/30 bg-teal/5 p-3 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={agreed} disabled={!url} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="policy-agree" />
              <span className="text-sm text-slate-700">{statement}</span>
            </label>
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-slate-400">Your name, date & time and device will be recorded.</p>
              <Button className="bg-navy shrink-0" disabled={!url || !agreed || accept.isPending} onClick={() => accept.mutate()} data-testid="policy-accept">
                {accept.isPending ? "Saving…" : "I Accept"}
              </Button>
            </div>
          </div>
        ) : policy.status === "Accepted" ? (
          <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> You accepted this version on {fmtDateTime(policy.acknowledged_at)}.</p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function MyPolicies() {
  const [reading, setReading] = useState(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["myPolicies"], queryFn: async () => (await api.get("/my/policies")).data,
  });

  const list = data?.policies || [];
  const counts = {
    todo: list.filter((p) => p.status === "Pending" || p.status === "Overdue").length,
    overdue: list.filter((p) => p.status === "Overdue").length,
  };

  return (
    <div>
      <PageHeader overline="Company Policies" title="My Policies" icon={ScrollText}
        subtitle="The rules that apply to you at IntenCiv. Please read each one carefully and accept it." />

      {isLoading ? <Loading /> : isError ? <ErrorState message="Couldn't load policies." onRetry={refetch} /> : list.length === 0 ? (
        <EmptyState icon={ScrollText} title="No policies yet" hint="When HR publishes a company policy, it will appear here." />
      ) : (<>
        {counts.todo > 0 ? (
          <div className={cn("mb-5 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
            counts.overdue ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800")}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>You have <span className="font-semibold">{counts.todo}</span> {counts.todo === 1 ? "policy" : "policies"} to read and accept.
              {counts.overdue > 0 && " Some are past the deadline, so you can't apply for leave or send correction requests until you accept them. Attendance still works as normal."}</p>
          </div>
        ) : (
          <div className="mb-5 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /><p>You're up to date — you have accepted every policy that applies to you. Thank you!</p>
          </div>
        )}
        <div className="mb-4 flex items-start gap-2 text-xs text-slate-500"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p>If anything in a policy is unclear, ask your Reporting Officer or HR before you accept it. You can come back and read any policy again at any time.</p></div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((p) => {
            const s = STATUS[p.status] || STATUS.Pending;
            const Icon = s.icon;
            const todo = p.status === "Pending" || p.status === "Overdue";
            return (
              <Card key={p.id} className={cn("p-4 shadow-none flex flex-col gap-3", p.status === "Overdue" ? "border-red-200" : "border-slate-200")} data-testid={`my-policy-${p.id}`}>
                <div>
                  <p className="overline text-slate-400">{p.category}</p>
                  <p className="font-semibold text-navy leading-snug">{p.title} <span className="text-xs text-slate-400 font-normal">v{p.version}</span></p>
                </div>
                {p.description && <p className="text-sm text-slate-600 line-clamp-3">{p.description}</p>}
                {p.accepted_previous_version && todo && <p className="text-xs text-amber-700">Updated — you accepted v{p.accepted_previous_version}. Please read the new version.</p>}
                <div className="mt-auto flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
                  <div>
                    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", s.cls)}><Icon className="h-3 w-3" />{s.label}</span>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {p.status === "Accepted" ? `on ${fmtDate(p.acknowledged_at)}` : todo && p.due_date ? `Accept by ${fmtDate(p.due_date)}` : ""}
                    </p>
                  </div>
                  <Button size="sm" className={todo ? "bg-navy" : "bg-teal hover:bg-teal/90 text-white"} onClick={() => setReading(p)}>
                    <BookOpen className="h-4 w-4 mr-1" /> {todo ? "Read & Accept" : "Read"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </>)}

      {/* `reading` is the object captured on click, so it stays the same reference
          while the list refreshes — the dialog's file fetch keys on it and must
          not re-run on every refresh. */}
      <ReadDialog policy={reading} statement={data?.statement} onClose={() => setReading(null)} />
    </div>
  );
}
